import { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BaseSheet } from "@/components/common/BaseSheet";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import { Agent } from "@/types/agent/agent";
import { GENIE_PURPOSES, getPurposeMeta, type GeniePurpose, type GeniePurposeMeta } from "@/types/genie-purpose";

interface GeniePurposePickerSheetProps {
  genie: Agent;
  isOpen: boolean;
  onClose: () => void;
  currentPurpose: GeniePurpose;
  onSelect: (purpose: GeniePurpose) => Promise<void> | void;
  isSaving?: boolean;
}

interface PurposeRowProps {
  meta: GeniePurposeMeta;
  isSelected: boolean;
  isCurrent: boolean;
  onSelect: () => void;
}

const PurposeRow = ({ meta, isSelected, isCurrent, onSelect }: PurposeRowProps) => {
  const rowInner = (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left p-4 flex items-center gap-3 transition-colors ${
        isSelected
          ? "rounded-[10px] bg-white"
          : "rounded-xl bg-white border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
      }`}
    >
      <div className="flex-shrink-0 w-11 h-11 rounded-lg flex items-center justify-center bg-gradient-to-br from-genie-primary/10 to-genie-secondary/10">
        <GenieIcon name={meta.genieIcon} size={24} className="w-6 h-6" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-gray-900">{meta.label} Genie</span>
          {isCurrent && (
            <span className="text-[10px] uppercase tracking-wide font-semibold text-gray-500">
              Current
            </span>
          )}
          {meta.comingSoon && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-medium text-gray-400">
              <span className="w-1 h-1 rounded-full bg-gray-300" />
              Inbox tab coming soon
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600 mt-0.5">{meta.description}</p>
      </div>

      {isSelected && (
        <Check className="w-5 h-5 text-genie-primary flex-shrink-0" strokeWidth={2.5} />
      )}
    </button>
  );

  if (isSelected) {
    // Signature brand gradient border (guidelines/design.md §2)
    return (
      <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-md shadow-genie-primary/20">
        {rowInner}
      </div>
    );
  }

  return rowInner;
};

export const GeniePurposePickerSheet = ({
  genie,
  isOpen,
  onClose,
  currentPurpose,
  onSelect,
  isSaving = false,
}: GeniePurposePickerSheetProps) => {
  const [selected, setSelected] = useState<GeniePurpose>(currentPurpose);

  useEffect(() => {
    if (isOpen) setSelected(currentPurpose);
  }, [isOpen, currentPurpose]);

  const currentMeta = getPurposeMeta(currentPurpose);

  const handleSave = async () => {
    await onSelect(selected);
  };

  return (
    <BaseSheet
      isOpen={isOpen}
      onClose={onClose}
      title={`What's ${genie.name}'s purpose?`}
      hideTitle
      size="lg"
      footer={
        <div className="flex justify-start gap-3 w-full">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || selected === currentPurpose}
            className="bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold px-6 shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:hover:scale-100 disabled:hover:shadow-lg"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Purpose"
            )}
          </Button>
        </div>
      }
    >
      {/* Branded header */}
      <div className="px-6 sm:px-8 pt-2 pb-6 border-b">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-genie-primary to-genie-secondary flex items-center justify-center shadow-md shadow-genie-primary/30">
            <GenieIcon name="genie-lamp" size={28} color="white" />
          </div>
          <div className="flex-1 min-w-0 pt-0.5">
            <h2 className="text-xl font-bold text-gray-900 leading-tight">
              What&rsquo;s {genie.name}&rsquo;s purpose?
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Pick the job this Genie is hired to do. Purpose drives where its conversations land in your Inbox.
            </p>
            <div className="mt-3 inline-flex items-center gap-2 text-xs">
              <span className="text-gray-500">Currently set to</span>
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-genie-primary/10 text-genie-primary font-semibold">
                <GenieIcon name={currentMeta.genieIcon} size={14} className="w-3.5 h-3.5" />
                {currentMeta.label}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Picker list */}
      <div className="px-6 sm:px-8 py-6 space-y-2.5">
        {GENIE_PURPOSES.map((meta) => (
          <PurposeRow
            key={meta.id}
            meta={meta}
            isSelected={selected === meta.id}
            isCurrent={currentPurpose === meta.id}
            onSelect={() => setSelected(meta.id)}
          />
        ))}

        <p className="text-xs text-gray-500 pt-4 leading-relaxed">
          Changing the Purpose doesn&rsquo;t move past conversations &mdash; they stay tagged with whichever
          Purpose was set at the time. Only new conversations route to the new Inbox tab.
        </p>
      </div>
    </BaseSheet>
  );
};
